import { useLanguage } from "@/contexts/LanguageContext";
import { Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";

interface Testimonial {
  company: string;
  country: string;
  text: string;
  author: string;
  position: string;
}

const TestimonialsSection = () => {
  const { t } = useLanguage();
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  const testimonials: Testimonial[] = [
    {
      company: "ProCredit Bank",
      country: t("testimonials.country.belarus"),
      text: t("testimonials.review1"),
      author: t("testimonials.author1"),
      position: t("testimonials.position1"),
    },
    {
      company: "Credit Portal",
      country: t("testimonials.country.belarus"),
      text: t("testimonials.review2"),
      author: t("testimonials.author2"),
      position: t("testimonials.position2"),
    },
    {
      company: "Uvils Leasing",
      country: t("testimonials.country.belarus"),
      text: t("testimonials.review3"),
      author: t("testimonials.author3"),
      position: t("testimonials.position3"),
    },
    {
      company: "FinBridge Capital",
      country: t("testimonials.country.russia"),
      text: t("testimonials.review4"),
      author: t("testimonials.author4"),
      position: t("testimonials.position4"),
    },
    {
      company: "TechnoLeasing",
      country: t("testimonials.country.russia"),
      text: t("testimonials.review5"),
      author: t("testimonials.author5"),
      position: t("testimonials.position5"),
    },
    {
      company: "Alfa Solutions",
      country: t("testimonials.country.russia"),
      text: t("testimonials.review6"),
      author: t("testimonials.author6"),
      position: t("testimonials.position6"),
    },
    {
      company: "KazFinance Group",
      country: t("testimonials.country.kazakhstan"),
      text: t("testimonials.review7"),
      author: t("testimonials.author7"),
      position: t("testimonials.position7"),
    },
    {
      company: "Astana Credit",
      country: t("testimonials.country.kazakhstan"),
      text: t("testimonials.review8"),
      author: t("testimonials.author8"),
      position: t("testimonials.position8"),
    },
    {
      company: "Eurasian Leasing",
      country: t("testimonials.country.kazakhstan"),
      text: t("testimonials.review9"),
      author: t("testimonials.author9"),
      position: t("testimonials.position9"),
    },
  ];

  useEffect(() => {
    if (!api) return;

    setCurrent(api.selectedScrollSnap());

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  const scrollPrev = useCallback(() => {
    api?.scrollPrev();
  }, [api]);

  const scrollNext = useCallback(() => {
    api?.scrollNext();
  }, [api]);

  const scrollTo = useCallback((index: number) => {
    api?.scrollTo(index);
  }, [api]);

  // Auto-play
  useEffect(() => {
    if (!api) return;

    const interval = setInterval(() => {
      if (api.canScrollNext()) {
        api.scrollNext();
      } else {
        api.scrollTo(0);
      }
    }, 6000);

    return () => clearInterval(interval);
  }, [api]);

  return (
    <section className="py-20 md:py-28 bg-primary overflow-hidden relative">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>
      
      <div className="container relative z-10">
        <div className="text-center mb-14">
          <span className="text-sm font-semibold uppercase tracking-wider text-primary-foreground/70 mb-3 block">
            {t("testimonials.label")}
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-primary-foreground mb-4">
            {t("testimonials.title")}
          </h2>
          <p className="text-lg text-primary-foreground/80 leading-relaxed max-w-2xl mx-auto">
            {t("testimonials.description")}
          </p>
        </div>

        <div className="relative max-w-6xl mx-auto">
          {/* Navigation Buttons */}
          <button
            onClick={scrollPrev}
            className="absolute -left-4 md:-left-12 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-primary-foreground hover:bg-white/20 transition-all duration-300 hover:scale-110"
            aria-label="Previous testimonial"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={scrollNext}
            className="absolute -right-4 md:-right-12 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-primary-foreground hover:bg-white/20 transition-all duration-300 hover:scale-110"
            aria-label="Next testimonial"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          {/* Carousel */}
          <Carousel
            setApi={setApi}
            opts={{
              align: "center",
              loop: true,
            }}
            className="w-full"
          >
            <CarouselContent className="-ml-4 md:-ml-6">
              {testimonials.map((testimonial, index) => (
                <CarouselItem
                  key={index}
                  className="pl-4 md:pl-6 basis-full md:basis-1/2 lg:basis-1/3"
                >
                  <div
                    className={`group relative h-full transition-all duration-500 ${
                      current === index ? 'scale-100 opacity-100' : 'scale-95 opacity-60'
                    }`}
                  >
                    {/* Card */}
                    <div className="relative h-full p-6 md:p-8 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300">
                      {/* Quote Mark */}
                      <div className="absolute -top-3 left-8">
                        <div className="w-10 h-10 rounded-full bg-primary-foreground flex items-center justify-center shadow-lg">
                          <Quote className="w-5 h-5 text-primary" />
                        </div>
                      </div>

                      {/* Content */}
                      <div className="pt-6 h-full flex flex-col">
                        {/* Company */}
                        <div className="mb-5">
                          <h3 className="font-bold text-primary-foreground text-lg">
                            {testimonial.company}
                          </h3>
                          <span className="text-xs text-primary-foreground/60 uppercase tracking-wider">
                            {testimonial.country}
                          </span>
                        </div>

                        {/* Testimonial Text */}
                        <p className="text-primary-foreground/90 leading-relaxed text-sm flex-grow mb-6 italic">
                          "{testimonial.text}"
                        </p>

                        {/* Author */}
                        <div className="flex items-center gap-4 pt-5 border-t border-white/10">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-white/20 to-white/5 flex items-center justify-center text-primary-foreground font-semibold text-sm border border-white/20">
                            {testimonial.author.split(' ').map(n => n[0]).join('')}
                          </div>
                          <div>
                            <p className="font-semibold text-primary-foreground text-sm">
                              {testimonial.author}
                            </p>
                            <p className="text-xs text-primary-foreground/60">
                              {testimonial.position}, {testimonial.company}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>

          {/* Dots Navigation */}
          <div className="flex justify-center gap-1 mt-10">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => scrollTo(index)}
                className="flex items-center justify-center min-w-[24px] min-h-[24px] p-2"
                aria-label={`Go to testimonial ${index + 1}`}
              >
                <span
                  className={`block h-2 rounded-full transition-all duration-300 ${
                    current === index 
                      ? 'w-8 bg-primary-foreground' 
                      : 'w-2 bg-primary-foreground/30 hover:bg-primary-foreground/50'
                  }`}
                />
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
